// Relatório de Cashback (Relatório de Ofertas → Cashback).
// Mostra: totais distribuído/usado/saldo, carteiras dos clientes com saldo
// e pedidos que consumiram cashback. Dados de GET /api/cashback/report.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Coins, HandCoins, PiggyBank,
  RefreshCw, Search, ShoppingBag, Users, Wallet,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { CarregandoCards, CarregandoLista } from "../components/carregando";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = "/api";
const PAGE_SIZE = 10;

const fmtBRL = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDataHora = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const [data, hora] = s.split("T");
  if (!data) return s;
  const [a, m, d] = data.split("-");
  return `${d}/${m}/${a}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

// Miniatura do cliente — uploads são servidos sob /api; erro cai no fallback
function FotoCliente({ src, nome }) {
  const [erro, setErro] = useState(false);
  const url = src && (src.startsWith("http") || src.startsWith("data:")) ? src : src ? `${API}${src}` : null;
  if (!url || erro) {
    return (
      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400 shrink-0">
        {(nome || "?").charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <img src={url} alt="" loading="lazy" onError={() => setErro(true)}
      className="w-8 h-8 rounded-full object-cover shrink-0" />
  );
}

// Paginação client-side: « 2 / 5 »
function Paginacao({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-3 mt-2 border-t border-border/50">
      <Button variant="outline" size="icon" className="h-7 w-7"
        disabled={page === 0} onClick={() => onChange(page - 1)} title="Página anterior">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-xs text-muted-foreground tabular-nums px-2">
        {page + 1} / {totalPages}
      </span>
      <Button variant="outline" size="icon" className="h-7 w-7"
        disabled={page >= totalPages - 1} onClick={() => onChange(page + 1)} title="Próxima página">
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function StatCard({ icon: Icon, cor, bg, label, valor, sub }) {
  return (
    <div className="bg-card rounded-xl border p-5 text-center">
      <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center mx-auto mb-3`}>
        <Icon className={`w-5 h-5 ${cor}`} strokeWidth={1.5} />
      </div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums">{valor}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function RelatorioCashback() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  // Carregamento com cards só na primeira abertura; "Atualizar" só gira o ícone.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [atualizando, setAtualizando] = useState(false);

  // Filtros e paginação — Carteiras
  const [clienteBusca, setClienteBusca] = useState("");
  const [clienteSort, setClienteSort] = useState("saldo-desc");
  const [clientePage, setClientePage] = useState(0);
  // Filtro e paginação — Pedidos
  const [pedidoBusca, setPedidoBusca] = useState("");
  const [pedidoPage, setPedidoPage] = useState(0);

  const clientesVisiveis = useMemo(() => {
    let lista = report?.clientes || [];
    if (clienteBusca.trim()) {
      const q = clienteBusca.trim().toLowerCase();
      lista = lista.filter((c) => (c.nome || "").toLowerCase().includes(q));
    }
    const cmp = {
      "saldo-desc": (a, b) => b.saldo - a.saldo,
      "ganho-desc": (a, b) => b.ganho - a.ganho,
      "ganho-asc": (a, b) => a.ganho - b.ganho,
      "usado-desc": (a, b) => b.usado - a.usado,
      "usado-asc": (a, b) => a.usado - b.usado,
    };
    return [...lista].sort(cmp[clienteSort] || cmp["saldo-desc"]);
  }, [report, clienteBusca, clienteSort]);

  const pedidosVisiveis = useMemo(() => {
    let lista = report?.pedidos || [];
    if (pedidoBusca.trim()) {
      const q = pedidoBusca.trim().toLowerCase().replace("#", "");
      lista = lista.filter((p) => (p.codigo || "").toLowerCase().replace("#", "").includes(q));
    }
    return lista;
  }, [report, pedidoBusca]);

  const clienteTotalPages = Math.ceil(clientesVisiveis.length / PAGE_SIZE);
  const pedidoTotalPages = Math.ceil(pedidosVisiveis.length / PAGE_SIZE);
  const clientesPagina = clientesVisiveis.slice(clientePage * PAGE_SIZE, (clientePage + 1) * PAGE_SIZE);
  const pedidosPagina = pedidosVisiveis.slice(pedidoPage * PAGE_SIZE, (pedidoPage + 1) * PAGE_SIZE);

  const fetchReport = async () => {
    setAtualizando(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/cashback/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
    } catch (e) {
      setReport(null);
    } finally {
      setPrimeiraCarga(false);
      setAtualizando(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const cfg = report?.config || {};

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <button
            type="button"
            onClick={() => navigate("/admin/impulsione/relatorio-ofertas")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" /> Relatório de Ofertas
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Relatório de Cashback</h1>
                {primeiraCarga ? <Skeleton className="h-4 w-64 mt-1" /> : report && (
                <p className="text-sm text-muted-foreground">
                  {cfg.ativo
                    ? `Cashback ativo — ${Number(cfg.percentual || 0).toLocaleString("pt-BR")}% sobre itens elegíveis`
                    : "Cashback desativado no momento (histórico abaixo)"}
                </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={atualizando} className="gap-2 shrink-0">
              <RefreshCw className={`w-4 h-4 ${atualizando && !primeiraCarga ? "animate-spin" : ""}`} /> Atualizar
            </Button>
          </div>
        </div>

        {primeiraCarga ? (
          // Carregamento com cards (25/10/2026): os 4 totais e as duas listas piscando.
          <>
            <CarregandoCards quantos={4} className="grid grid-cols-2 lg:grid-cols-4 gap-4" altura="h-[164px]" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CarregandoLista linhas={6} colunas={1} />
              <CarregandoLista linhas={6} avatar={false} colunas={1} />
            </div>
          </>
        ) : !report ? (
          <div className="p-6 text-center text-muted-foreground">
            <p>Não foi possível carregar o relatório de cashback.</p>
            <Button onClick={fetchReport} className="mt-4">Tentar novamente</Button>
          </div>
        ) : (<>

        {/* Totais */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={HandCoins} cor="text-emerald-500" bg="bg-emerald-500/10"
            label="Total distribuído" valor={fmtBRL(report.total_distribuido)}
            sub="créditos concedidos aos clientes" />
          <StatCard icon={ShoppingBag} cor="text-orange-500" bg="bg-orange-500/10"
            label="Total usado" valor={fmtBRL(report.total_usado)}
            sub={`em ${report.pedidos_count} pedido(s)`} />
          <StatCard icon={PiggyBank} cor="text-blue-500" bg="bg-blue-500/10"
            label="Saldo em carteiras" valor={fmtBRL(report.saldo_carteiras)}
            sub="crédito ainda não resgatado" />
          <StatCard icon={Users} cor="text-violet-500" bg="bg-violet-500/10"
            label="Clientes com saldo" valor={report.clientes_com_saldo}
            sub="carteiras ativas" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Carteiras dos clientes */}
          <div className="bg-card rounded-xl border p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <Wallet className="w-4 h-4 text-emerald-500" />
              Carteiras com saldo
              <span className="text-[11px] text-muted-foreground font-normal ml-auto tabular-nums">
                {clientesVisiveis.length} cliente(s)
              </span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome..."
                  value={clienteBusca}
                  onChange={(e) => { setClienteBusca(e.target.value); setClientePage(0); }}
                  className="pl-8 h-9 text-sm"
                />
              </div>
              <Select value={clienteSort} onValueChange={(v) => { setClienteSort(v); setClientePage(0); }}>
                <SelectTrigger className="w-full sm:w-44 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="saldo-desc">Maior saldo</SelectItem>
                  <SelectItem value="ganho-desc">Mais ganhou</SelectItem>
                  <SelectItem value="ganho-asc">Menos ganhou</SelectItem>
                  <SelectItem value="usado-desc">Mais gastou</SelectItem>
                  <SelectItem value="usado-asc">Menos gastou</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {clientesPagina.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {clienteBusca ? "Nenhum cliente encontrado com esse nome" : "Nenhum cliente com saldo de cashback no momento"}
              </p>
            ) : (
              <div className="space-y-0.5">
                {clientesPagina.map((c, i) => (
                  <div key={c.cliente_id || i} className="flex items-center gap-2.5 py-1.5 border-b border-border/50 last:border-0">
                    <FotoCliente src={c.foto} nome={c.nome} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium truncate">{c.nome}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {c.telefone || "sem telefone"} · ganhou {fmtBRL(c.ganho)} · usou {fmtBRL(c.usado)}
                      </p>
                    </div>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums text-sm shrink-0">
                      {fmtBRL(c.saldo)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Paginacao page={clientePage} totalPages={clienteTotalPages} onChange={setClientePage} />
          </div>

          {/* Pedidos que usaram cashback */}
          <div className="bg-card rounded-xl border p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <ShoppingBag className="w-4 h-4 text-orange-500" />
              Pedidos que usaram cashback
              <span className="text-[11px] text-muted-foreground font-normal ml-auto tabular-nums">
                {report.pedidos_count > report.pedidos.length
                  ? `${report.pedidos.length} mais recentes de ${report.pedidos_count}`
                  : `${pedidosVisiveis.length} pedido(s)`}
              </span>
            </h3>
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por código do pedido..."
                value={pedidoBusca}
                onChange={(e) => { setPedidoBusca(e.target.value); setPedidoPage(0); }}
                className="pl-8 h-9 text-sm"
              />
            </div>
            {pedidosPagina.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {pedidoBusca ? "Nenhum pedido encontrado com esse código" : "Nenhum pedido usou cashback ainda"}
              </p>
            ) : (
              <div className="space-y-0.5">
                {pedidosPagina.map((p, i) => (
                  <div key={`${p.codigo}-${i}`} className="flex items-center gap-2.5 py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium truncate">
                        {p.codigo} · {p.cliente_nome || "Cliente"}
                        {p.status === "cancelado" && (
                          <span className="ml-1.5 text-[10px] font-bold uppercase text-red-500">cancelado</span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {fmtDataHora(p.created_at)} · pedido de {fmtBRL(p.total)}
                      </p>
                    </div>
                    <span className="font-bold text-orange-600 dark:text-orange-400 tabular-nums text-sm shrink-0">
                      −{fmtBRL(p.cashback_usado)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Paginacao page={pedidoPage} totalPages={pedidoTotalPages} onChange={setPedidoPage} />
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}
